import React, { useEffect, useMemo, useState } from "react";
import { Select, Avatar, Space, Tag } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { Modal, Form, Button } from "react-bootstrap";
import api from "../../services/api";
import { notify } from "../../services/toast.config";
import { getEmployeeImageUrl } from "../../feature-module/application/todo/services/todoCommanService";

interface Member {
  id: string;
  name: string;
}

interface TransferTask {
  id?: string;
  title: string;
  company_id: string;
  company_type: string;
  assigned_members: Array<{ id?: string | number; name?: string; [key: string]: any }>;
}

interface MemberOption {
  label: string;
  value: string;
  image_url: string;
}

interface TransferTaskModalProps {
  /** The task being transferred. `null` keeps the modal closed. */
  todo: TransferTask | null;
  signature: string | null;
  onClose: () => void;
  /** Called after a successful transfer so the parent can refresh its state. */
  onTransferred: (
    todoId: string,
    fromMemberIds: string[],
    toMembers: Member[]
  ) => void;
}

const capitalizeFirstLetter = (str: string) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

const TransferTaskModal: React.FC<TransferTaskModalProps> = ({
  todo,
  signature,
  onClose,
  onTransferred,
}) => {
  const [users, setUsers] = useState<any[]>([]);
  const [fromMemberIds, setFromMemberIds] = useState<string[]>([]);
  const [toUserIds, setToUserIds] = useState<string[]>([]);
  const [reason, setReason] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  // Members come from the SAME source as the Add New Task modal so the lists
  // (and avatars) are identical. Fetched lazily the first time the modal opens.
  useEffect(() => {
    if (!todo || users.length > 0) return;
    const fetchUsers = async () => {
      try {
        const response = await api.post(
          "userService/getActiveEmployeesListData",
          { signature }
        );
        setUsers(response?.data?.data?.data?.allUsers || []);
      } catch (error) {
        notify.error("Failed to load members");
      }
    };
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todo, signature]);

  // Reset the form whenever a new task is opened/closed
  useEffect(() => {
    setFromMemberIds([]);
    setToUserIds([]);
    setReason("");
  }, [todo?.id]);

  // Users belonging to the task's company
  const companyUsers = useMemo(() => {
    if (!todo) return [];
    return users.filter((user: any) => {
      const userCompanyId =
        typeof user.CompanyId === "string"
          ? parseInt(user.CompanyId)
          : user.CompanyId;
      const taskCompanyId =
        typeof todo.company_id === "string"
          ? parseInt(todo.company_id)
          : todo.company_id;
      return (
        userCompanyId === taskCompanyId && user.CompanyType === todo.company_type
      );
    });
  }, [users, todo]);

  // Current assigned members → "transfer from" options
  const fromOptions = useMemo<MemberOption[]>(() => {
    if (!todo) return [];
    return (todo.assigned_members || []).map((m) => {
      const id = String(m.id ?? "");
      return {
        label: m.name || "",
        value: id,
        image_url: getEmployeeImageUrl(users, id) || "assets/img/users/user.png",
      };
    });
  }, [todo, users]);

  // Company users that are NOT staying on the task → "transfer to" options
  const toOptions = useMemo<MemberOption[]>(() => {
    if (!todo) return [];
    const keptAssignedIds = new Set(
      (todo.assigned_members || [])
        .map((m) => m.id?.toString())
        .filter((mid) => !fromMemberIds.includes(mid as string))
    );
    return companyUsers
      .filter((u: any) => !keptAssignedIds.has(u.EmpId?.toString()))
      .map((u: any) => ({
        label: u.Name,
        value: u.EmpId?.toString(),
        image_url:
          getEmployeeImageUrl(users, u.EmpId) || "assets/img/users/user.png",
      }));
  }, [todo, fromMemberIds, companyUsers, users]);

  // Render each selected member as a readable chip with their avatar
  const renderMemberTag =
    (options: MemberOption[]) =>
    (props: any) => {
      const { label, value, closable, onClose: onTagClose } = props;
      const opt = options.find(
        (o) => o.value?.toString() === value?.toString()
      );
      return (
        <Tag
          closable={closable}
          onClose={onTagClose}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            paddingInline: 6,
            marginInlineEnd: 4,
            marginBlock: 2,
            borderRadius: 20,
          }}
        >
          <Avatar
            size={20}
            src={opt?.image_url || undefined}
            icon={<UserOutlined />}
          />
          <span style={{ color: "rgba(0,0,0,0.88)", fontSize: 13 }}>
            {label}
          </span>
        </Tag>
      );
    };

  const handleTransfer = async () => {
    if (!todo) return;
    if (fromMemberIds.length === 0) {
      notify.error("Please select at least one member to transfer from");
      return;
    }
    if (toUserIds.length === 0) {
      notify.error("Please select at least one member to transfer to");
      return;
    }

    const to_members = toUserIds
      .map((uid) => {
        const toUser: any = users.find(
          (u: any) => u.EmpId?.toString() === uid?.toString()
        );
        if (!toUser) return null;
        return {
          id: toUser.EmpId?.toString(),
          name: toUser.Name,
          avatar: toUser.Image || "",
          email: toUser.Email || "",
          mobile_no:
            (toUser.Official_Phone_code || "") +
            (toUser.Official_Phone_Number || ""),
        };
      })
      .filter(Boolean) as Array<{ id: string; name: string }>;

    if (to_members.length === 0) {
      notify.error("Selected target members not found");
      return;
    }

    try {
      setLoading(true);
      const response = await api.post("todoService/transferTodo", {
        signature,
        id: todo.id,
        from_member_ids: fromMemberIds,
        to_members,
        reason: reason || "",
      });

      notify.success(
        response?.data?.message?.[0]?.msg || "Task transferred successfully"
      );
      onTransferred(todo.id || "", fromMemberIds, to_members);
      onClose();
    } catch (error: any) {
      notify.error(error?.data?.message?.[0]?.msg || "Failed to transfer task");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={!!todo} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Transfer Task</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {todo && (
          <Form>
            <div className="mb-3">
              <small className="text-muted">Task</small>
              <div className="fw-medium">
                {capitalizeFirstLetter(todo.title)}
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>
                Transfer From <span className="text-danger">*</span>
              </Form.Label>
              <Select
                mode="multiple"
                size="large"
                style={{ width: "100%" }}
                placeholder="Select members to transfer"
                allowClear
                showSearch
                value={fromMemberIds}
                onChange={(values) => {
                  setFromMemberIds(values);
                  setToUserIds((prev) =>
                    prev.filter((id) => !values.includes(id))
                  );
                }}
                dropdownStyle={{ zIndex: 9999 }}
                filterOption={(input, option: any) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={fromOptions}
                tagRender={renderMemberTag(fromOptions)}
                optionRender={(option) => (
                  <Space>
                    <Avatar
                      size="small"
                      src={option?.data?.image_url || undefined}
                      icon={<UserOutlined />}
                    />
                    <span>{option.label}</span>
                  </Space>
                )}
              />
              <small className="text-muted">
                Members removed from the task.
              </small>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Transfer To <span className="text-danger">*</span>
              </Form.Label>
              <Select
                mode="multiple"
                size="large"
                style={{ width: "100%" }}
                placeholder="Select members to assign"
                allowClear
                showSearch
                value={toUserIds}
                onChange={(values) => setToUserIds(values)}
                dropdownStyle={{ zIndex: 9999 }}
                filterOption={(input, option: any) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={toOptions}
                tagRender={renderMemberTag(toOptions)}
                optionRender={(option) => (
                  <Space>
                    <Avatar
                      size="small"
                      src={option?.data?.image_url || undefined}
                      icon={<UserOutlined />}
                    />
                    <span>{option.label}</span>
                  </Space>
                )}
              />
              <small className="text-muted">
                New members who take over the task.
              </small>
            </Form.Group>

            <Form.Group className="mb-2">
              <Form.Label>Reason (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is this task being transferred?"
              />
            </Form.Group>

            <div className="alert alert-info mb-0">
              <i className="ti ti-info-circle me-2"></i>
              The new member will be notified and this transfer will be recorded
              in the task logs.
            </div>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleTransfer}
          disabled={
            loading || fromMemberIds.length === 0 || toUserIds.length === 0
          }
        >
          {loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Transferring...
            </>
          ) : (
            <>
              <i className="ti ti-transfer me-1"></i> Transfer Task
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TransferTaskModal;
